<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');

const isLoading = ref(true);
const isSubmitting = ref(false);
const preAuth = ref(null);
const errorMessage = ref('');
const errorTitle = ref('');

// OSMCP-03: com o MCP endurecido, só super-admin consente. A recusa chega como
// 403 `{ error: { code: 'super_admin_required' } }`, e a tela diz isso com todas
// as letras: a mensagem genérica ("pedido inválido") mandaria a pessoa procurar
// defeito na aplicação, quando o que falta é a permissão dela.
const traduzirErro = error => {
  if (error?.response?.data?.error?.code === 'super_admin_required') {
    errorTitle.value = t('OAUTH_CONSENT.SUPER_ADMIN_REQUIRED_TITLE');
    return t('OAUTH_CONSENT.SUPER_ADMIN_REQUIRED');
  }
  return (
    error?.response?.data?.error_description || t('OAUTH_CONSENT.ERROR_GENERIC')
  );
};

// Os parâmetros vêm do cliente e voltam para o Doorkeeper como chegaram —
// `state` e `code_challenge` inclusive, que o cliente confere na volta. Reenviar
// a query original é o que garante que nada se perca no caminho pela tela.
const query = new URLSearchParams(window.location.search);

const scopes = computed(() =>
  (preAuth.value?.scope || '').split(' ').filter(Boolean)
);

const scopeLabel = scope => {
  switch (scope) {
    case 'powerdata:read':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERDATA_READ');
    case 'powerdata:write':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERDATA_WRITE');
    case 'poweragents:read':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERAGENTS_READ');
    case 'poweragents:write':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERAGENTS_WRITE');
    case 'poweragents:test':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERAGENTS_TEST');
    case 'poweragents:publish':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERAGENTS_PUBLISH');
    case 'powerflow:run':
      return t('OAUTH_CONSENT.SCOPE_LABELS.POWERFLOW_RUN');
    // Escopo desconhecido aparece cru em vez de sumir: uma permissão que a tela
    // não sabe descrever ainda é uma permissão que o usuário está concedendo.
    default:
      return scope;
  }
};

const carregar = async () => {
  try {
    // `Accept: application/json` explicito, e nao por acaso: sem ele o pedido
    // chega ao Rails como `html`, o controller o trata como navegacao e devolve
    // um 302 para ESTA tela. O axios segue o redirect e `data` vira a pagina
    // HTML — `client_name` e `scope` saem `undefined`, e a tela pede permissao
    // sem dizer de quem nem para que. O servidor tambem foi corrigido; os dois
    // lados, porque um bundle em cache nao deve poder reviver o defeito.
    const { data } = await window.axios.get(
      `/oauth/authorize?${query.toString()}`,
      { headers: { Accept: 'application/json' } }
    );
    // Quando já existe consentimento válido o Doorkeeper responde com o destino
    // em vez da pré-autorização; nesse caso não há o que perguntar.
    if (data.redirect_uri && data.status === 'redirect') {
      window.location.assign(data.redirect_uri);
      return;
    }
    preAuth.value = data;
  } catch (error) {
    errorMessage.value = traduzirErro(error);
  } finally {
    isLoading.value = false;
  }
};

const decidir = async metodo => {
  isSubmitting.value = true;
  try {
    const { data } = await window.axios({
      method: metodo,
      url: `/oauth/authorize?${query.toString()}`,
      headers: { Accept: 'application/json' },
    });
    // Tanto o "conectar" quanto o "cancelar" terminam no cliente: cancelar
    // devolve `access_denied` pelo mesmo redirect, que é como o cliente fica
    // sabendo da recusa em vez de esperar para sempre.
    if (data.redirect_uri) {
      window.location.assign(data.redirect_uri);
      return;
    }
    errorMessage.value = t('OAUTH_CONSENT.ERROR_GENERIC');
  } catch (error) {
    // Recusar responde 400 com o destino no corpo — o axios trata como erro,
    // mas para o fluxo é um desfecho normal: o cliente precisa receber o
    // `access_denied` para saber que foi recusado em vez de esperar sem fim.
    const destino = error?.response?.data?.redirect_uri;
    if (destino) {
      window.location.assign(destino);
      return;
    }
    errorMessage.value = traduzirErro(error);
  } finally {
    isSubmitting.value = false;
  }
};

onMounted(carregar);
</script>

<template>
  <div
    class="flex items-center justify-center w-full h-full min-h-screen p-6 bg-n-background"
  >
    <div
      class="flex flex-col w-full max-w-md gap-6 p-8 border rounded-2xl border-n-weak bg-n-solid-1 shadow-lg"
    >
      <div v-if="isLoading" class="flex flex-col items-center gap-3 py-6">
        <Spinner />
        <span class="text-sm text-n-slate-11">
          {{ $t('OAUTH_CONSENT.LOADING') }}
        </span>
      </div>

      <template v-else-if="errorMessage">
        <h1 class="mb-0 text-lg font-medium text-n-slate-12">
          {{ errorTitle || $t('OAUTH_CONSENT.ERROR_TITLE') }}
        </h1>
        <p class="mb-0 text-sm text-n-slate-11">{{ errorMessage }}</p>
      </template>

      <template v-else>
        <div class="flex flex-col gap-1">
          <h1 class="mb-0 text-lg font-medium text-n-slate-12">
            {{ $t('OAUTH_CONSENT.TITLE', { app: preAuth.client_name }) }}
          </h1>
          <span class="text-sm text-n-slate-11">
            {{ $t('OAUTH_CONSENT.SUBTITLE', { email: currentUser.email }) }}
          </span>
        </div>

        <div class="flex flex-col gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('OAUTH_CONSENT.PERMISSIONS') }}
          </span>
          <ul class="flex flex-col gap-2 pl-0 mb-0 list-none">
            <li
              v-for="scope in scopes"
              :key="scope"
              class="flex items-start gap-2 text-sm text-n-slate-11"
            >
              <span class="i-lucide-check mt-0.5 shrink-0 text-n-teal-10" />
              <span>{{ scopeLabel(scope) }}</span>
            </li>
          </ul>
        </div>

        <p class="mb-0 text-xs text-n-slate-10">
          {{ $t('OAUTH_CONSENT.REVOKE_NOTE') }}
        </p>

        <div class="flex flex-row justify-end gap-2">
          <NextButton
            :label="$t('OAUTH_CONSENT.DENY')"
            :disabled="isSubmitting"
            slate
            faded
            type="button"
            @click="decidir('delete')"
          />
          <NextButton
            :label="$t('OAUTH_CONSENT.ALLOW')"
            :is-loading="isSubmitting"
            type="button"
            @click="decidir('post')"
          />
        </div>
      </template>
    </div>
  </div>
</template>
